import {
  type ForwardedRef,
  type ReactNode,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from 'react';

import { cn } from '../../../utils';
import type { FlashListProps, ListChild, ScrollEvent } from '../FlashListProps';
import type {
  FlashListRef,
  ScrollToEndOptions,
  ScrollToIndexOptions,
  ScrollToItemOptions,
  ScrollToOffsetOptions,
} from '../FlashListRef';
import type { EngagedRange } from './EngagedIndicesTracker';
import { RecyclerViewManager } from './RecyclerViewManager';
import { ViewHolderCollection } from './ViewHolderCollection';
import { StickyHeaders } from './components/StickyHeaders';
import { useInvertedScrollFix } from './hooks/useInvertedScrollFix';

const SCROLLER_TEST_ID = 'flash-list-scroller';
const CONTENT_TEST_ID = 'flash-list-content';
const ITEMS_TEST_ID = 'flash-list-items';
const HEADER_TEST_ID = 'flash-list-header';
const FOOTER_TEST_ID = 'flash-list-footer';
const EMPTY_TEST_ID = 'flash-list-empty';

// Web equivalent of upstream's PlatformConfig.invertedTransformStyle
// (src/native/config/PlatformHelper.web.ts).
const INVERTED_TRANSFORM = 'scaleY(-1)';

const DEFAULT_END_REACHED_THRESHOLD = 0.5;
const DEFAULT_START_REACHED_THRESHOLD = 0.5;
// Window during which the engine keeps re-snapping scrollTop to a captured
// sticky anchor after a data change. Long enough for buffered-item
// ResizeObserver measurements above the anchor to settle, short enough
// that the user feels free to scroll again immediately after. The
// re-snaps fire on natural renders (measurements coming in); we don't
// force renders, so an in-progress rubber-band that doesn't trigger any
// other state change may briefly drift scrollTop. The drift self-
// corrects on the next natural render and is barely visible in practice.
const STICKY_ANCHOR_DURATION_MS = 200;

function resolveListChild(child: ListChild | undefined): ReactNode {
  if (child == null) return null;
  if (typeof child === 'function') return child();
  return child;
}

function sameRange(a: EngagedRange | null, b: EngagedRange | null): boolean {
  if (a === null && b === null) return true;
  if (a === null || b === null) return false;
  return a.start === b.start && a.end === b.end;
}

export function RecyclerView<T>({
  data,
  renderItem,
  keyExtractor,
  getItemType,
  overrideItemLayout,
  numColumns,
  horizontal,
  masonry,
  optimizeItemArrangement,
  defaultItemSize,
  drawDistance,
  maxItemsInRecyclePool,
  inverted,
  invertedScrollFix,
  ListHeaderComponent,
  ListFooterComponent,
  ListEmptyComponent,
  ItemSeparatorComponent,
  stickyHeaderIndices,
  onScroll,
  onEndReached,
  onEndReachedThreshold,
  onStartReached,
  onStartReachedThreshold,
  onViewableItemsChanged,
  viewabilityConfig,
  maintainVisibleContentPosition,
  pagingEnabled,
  snapToAlignment,
  snapToInterval,
  snapToOffsets,
  className,
  contentContainerClassName,
  ref,
}: FlashListProps<T> & { ref?: ForwardedRef<FlashListRef<T>> }) {
  const [manager] = useState(
    () =>
      new RecyclerViewManager<T>({
        defaultItemSize,
        drawDistance,
        maxItemsInRecyclePool,
        numColumns,
        horizontal,
        masonry,
        optimizeItemArrangement,
      }),
  );
  const [mounted, setMounted] = useState(false);
  const [, bumpRender] = useReducer((n: number) => n + 1, 0);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const firstItemOffsetRef = useRef(0);
  const lastRenderedRangeRef = useRef<EngagedRange | null>(null);

  // ─── maintainVisibleContentPosition ──────────────────────────────────
  // Tracks state needed to keep the visible cells anchored across data
  // changes (chat-list pattern). Two adjustments may apply per data change:
  //   - Anchor preservation: shift scrollTop so the previously-visible cell
  //     stays at the same on-screen position when items appear above.
  //   - Auto-scroll to bottom: if the user was within
  //     `autoscrollToBottomThreshold` of the bottom before the update and
  //     items appended, scroll to the new end (typical "stay pinned to
  //     latest" chat behavior).
  // Both are computed during render (while we still have access to the
  // pre-update layout) and applied in useLayoutEffect before paint.
  const previousDataRef = useRef<ReadonlyArray<T>>(data);
  const pendingScrollAdjustmentRef = useRef(0);
  const pendingAutoscrollToBottomRef = useRef(false);
  // Sticky anchor — survives across renders after a data change so that
  // late-arriving item measurements (items above the anchor in the
  // draw-distance buffer) can re-snap scrollTop to keep the anchor pinned
  // at the same viewport position.
  //
  // Time-bounded (not scroll-event-bounded): on macOS rubber-band, the
  // browser fires extra scroll events with offsets that don't match our
  // programmatic target, and tying release to "user scroll" would let
  // bounce events defeat the preservation. The fixed window is long enough
  // for bounce + buffered-item measurements to settle, short enough that
  // the user feels free to scroll again immediately after.
  const stickyAnchorRef = useRef<{
    key: string;
    viewportOffsetFromAnchor: number;
    expiresAt: number;
  } | null>(null);
  // "Stick to bottom" mode — engaged by both startRenderingFromBottom
  // (initial mount) and autoscrollToBottomThreshold (data update while
  // near-bottom). While engaged, every useLayoutEffect re-snaps scrollTop
  // to the current bottom so the user stays at the latest as items measure
  // and content size grows. Disengages the first time the user genuinely
  // scrolls (detected via offset != `lastProgrammaticScrollOffset`).
  //
  // We use instant scrolls throughout — `animateAutoScrollToBottom` is
  // accepted for API parity with upstream but is currently a no-op on web.
  // Smooth scroll would interleave badly with measurement-driven re-snaps;
  // correctness wins over animation for now.
  const stickToBottomRef = useRef(false);
  const stickInitiallyEngagedRef = useRef(false);
  const lastProgrammaticScrollOffsetRef = useRef<number>(Number.NaN);

  const maintainPositionEnabled =
    maintainVisibleContentPosition != null &&
    maintainVisibleContentPosition.disabled !== true;
  const autoscrollToBottomThreshold =
    maintainVisibleContentPosition?.autoscrollToBottomThreshold ?? -1;
  const previousData = previousDataRef.current;
  const dataChanged = previousData !== data;

  if (maintainPositionEnabled && dataChanged && previousData.length > 0) {
    const scroller = scrollerRef.current;
    if (scroller) {
      const scrollOffset = horizontal
        ? scroller.scrollLeft
        : scroller.scrollTop;
      const viewport = horizontal
        ? scroller.clientWidth
        : scroller.clientHeight;
      const contentSize = horizontal
        ? scroller.scrollWidth
        : scroller.scrollHeight;

      // Decide whether we'll need to auto-scroll to the new bottom. This
      // has to read pre-update scroll/contentSize because after setData
      // the content has grown.
      if (
        !horizontal &&
        autoscrollToBottomThreshold >= 0 &&
        data.length > previousData.length
      ) {
        const thresholdPx = autoscrollToBottomThreshold * viewport;
        const distanceFromBottom = contentSize - viewport - scrollOffset;
        if (distanceFromBottom <= thresholdPx) {
          pendingAutoscrollToBottomRef.current = true;
        }
      }

      // Anchor preservation — find the first visible cell, capture its
      // axis position, run setData (which recomputes layouts), then read
      // the same cell's new axis position to derive the scroll delta.
      if (viewport > 0) {
        const range = manager.layoutManager.findIndicesInRange(
          scrollOffset,
          scrollOffset + viewport,
        );
        if (range && range.start < previousData.length) {
          const anchorIndex = range.start;
          const anchorKey = manager.keyAt(anchorIndex);
          const anchorAxisPos = horizontal
            ? manager.layoutManager.getLayout(anchorIndex).x
            : manager.layoutManager.getLayout(anchorIndex).y;
          const viewportOffsetFromAnchor = scrollOffset - anchorAxisPos;

          manager.setData(data, keyExtractor, getItemType, overrideItemLayout);

          const newIndex = data.findIndex(
            (item, index) =>
              (keyExtractor?.(item, index) ?? String(index)) === anchorKey,
          );
          if (newIndex >= 0) {
            // Measurements are index-keyed; without remapping, a prepend
            // would leave old item heights at slots now owned by the new
            // items at the top. Shifting by the same delta the anchor
            // moved keeps each measurement aligned to its data.
            const measurementShift = newIndex - anchorIndex;
            if (measurementShift !== 0) {
              manager.layoutManager.shiftMeasurements(measurementShift);
            }
            // Track the anchor across subsequent renders. Items in the
            // draw-distance buffer above the anchor render after this
            // commit, then their ResizeObserver measurements come in and
            // shift the layout — the anchor's actual y moves while
            // scrollTop is still at the original adjustment. Re-anchoring
            // on each natural render until the window expires keeps the
            // visible position stable while measurements settle.
            stickyAnchorRef.current = {
              key: anchorKey,
              viewportOffsetFromAnchor,
              expiresAt: Date.now() + STICKY_ANCHOR_DURATION_MS,
            };
          }
        } else {
          manager.setData(data, keyExtractor, getItemType, overrideItemLayout);
        }
      } else {
        manager.setData(data, keyExtractor, getItemType, overrideItemLayout);
      }
    } else {
      manager.setData(data, keyExtractor, getItemType, overrideItemLayout);
    }
  } else {
    manager.setData(data, keyExtractor, getItemType, overrideItemLayout);
  }
  previousDataRef.current = data;

  // Sticky anchor: every render (not only when data changes), re-snap
  // scrollTop so the tracked item stays at the captured viewport offset.
  // The data-change block above seeds `stickyAnchorRef`; this block applies
  // the actual scroll adjustment, both for the initial post-change render
  // and for any subsequent renders where buffered-item measurements
  // shifted the anchor's `y`. Cleared by `handleScroll` on a user-driven
  // scroll, by the autoscroll path (which has its own target), or when the
  // anchor key disappears from the data.
  if (stickyAnchorRef.current) {
    if (Date.now() >= stickyAnchorRef.current.expiresAt) {
      stickyAnchorRef.current = null;
    } else {
      const scroller = scrollerRef.current;
      if (scroller) {
        const { key, viewportOffsetFromAnchor } = stickyAnchorRef.current;
        const anchorIndex = data.findIndex(
          (item, index) =>
            (keyExtractor?.(item, index) ?? String(index)) === key,
        );
        if (anchorIndex < 0) {
          stickyAnchorRef.current = null;
        } else {
          const anchorLayout = manager.layoutManager.getLayout(anchorIndex);
          const anchorY = horizontal ? anchorLayout.x : anchorLayout.y;
          const desiredOffset = anchorY + viewportOffsetFromAnchor;
          const currentOffset = horizontal
            ? scroller.scrollLeft
            : scroller.scrollTop;
          const inFlight = pendingScrollAdjustmentRef.current;
          const expectedAfterPending = currentOffset + inFlight;
          const drift = desiredOffset - expectedAfterPending;
          if (Math.abs(drift) > 0.5) {
            pendingScrollAdjustmentRef.current += drift;
          }
        }
      }
    }
  }

  if (drawDistance != null) manager.setDrawDistance(drawDistance);
  manager.setViewability({
    viewabilityConfig,
    onViewableItemsChanged,
  });

  const endReachedFiredRef = useRef(false);
  const startReachedFiredRef = useRef(false);
  // Don't fire onStartReached immediately on mount (scroll is already at 0).
  const startSeenAwayFromTopRef = useRef(false);

  const measureBatchRef = useRef<
    Map<number, { width: number; height: number }>
  >(new Map());
  const measureScheduledRef = useRef(false);

  const onMeasure = (
    index: number,
    dimensions: { width: number; height: number },
  ) => {
    measureBatchRef.current.set(index, dimensions);
    if (measureScheduledRef.current) return;
    measureScheduledRef.current = true;
    queueMicrotask(() => {
      measureScheduledRef.current = false;
      const batch = measureBatchRef.current;
      if (batch.size === 0) return;
      const infos = Array.from(batch.entries(), ([index, dimensions]) => ({
        index,
        dimensions,
      }));
      batch.clear();
      manager.layoutManager.processLayoutInfo(infos);
      bumpRender();
    });
  };

  const lastMeasuredWidthRef = useRef(-1);
  const lastMeasuredHeightRef = useRef(-1);

  // Re-measure the scroller on every render. Matches upstream FlashList's
  // un-keyed `useLayoutEffect` (src/recyclerview/RecyclerView.tsx:165).
  // Cheap thanks to the dimension diff. First render may see clientWidth=0
  // (e.g. fresh Storybook iframe); a subsequent render — triggered either by
  // the ResizeObserver below or by data/scroll updates — catches the real
  // value.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const width = scroller.clientWidth;
    const height = scroller.clientHeight;
    if (width === 0) return;
    if (
      width === lastMeasuredWidthRef.current &&
      height === lastMeasuredHeightRef.current
    ) {
      return;
    }
    lastMeasuredWidthRef.current = width;
    lastMeasuredHeightRef.current = height;
    manager.setWindowSize({ width, height });
    const headerRect = headerRef.current?.getBoundingClientRect();
    firstItemOffsetRef.current = headerRect
      ? horizontal
        ? headerRect.width
        : headerRect.height
      : 0;
    setMounted(true);
    bumpRender();
  });

  useInvertedScrollFix(
    scrollerRef,
    inverted === true && invertedScrollFix === true,
  );

  // Apply pending anchor-preservation adjustment, then translate any
  // pending auto-scroll-to-bottom into a stick-to-bottom engage. The actual
  // scroll happens in the snap effect below — a single code path handles
  // initial snap + measurement-driven follow-up.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    if (pendingScrollAdjustmentRef.current !== 0) {
      const current = horizontal ? scroller.scrollLeft : scroller.scrollTop;
      const next = current + pendingScrollAdjustmentRef.current;
      lastProgrammaticScrollOffsetRef.current = next;
      if (horizontal) scroller.scrollLeft = next;
      else scroller.scrollTop = next;
      pendingScrollAdjustmentRef.current = 0;
    }

    if (pendingAutoscrollToBottomRef.current) {
      pendingAutoscrollToBottomRef.current = false;
      stickToBottomRef.current = true;
      // Auto-scroll-to-bottom and sticky-anchor model competing intents
      // (stay-at-latest vs stay-with-content); auto-scroll wins.
      stickyAnchorRef.current = null;
    }
  });

  // Snap-to-bottom: the single mechanism that drives both
  // startRenderingFromBottom (initial mount) and autoscrollToBottomThreshold
  // (data updates while near-bottom). Engages on the first layout pass
  // after either trigger and keeps re-snapping until the user scrolls away.
  //
  // This handles measurement-driven content growth — when a new item mounts
  // and its real height differs from the rolling-average estimate, the
  // layout manager's content size grows, and we re-snap to the new bottom.
  // Without this, the user lands "near" but not "at" the bottom.
  useLayoutEffect(() => {
    if (!maintainPositionEnabled || !mounted || data.length === 0) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;

    // Engage on first mount when startRenderingFromBottom is on (one-shot —
    // subsequent renders rely on stickToBottomRef staying true until the
    // user disengages it).
    if (
      maintainVisibleContentPosition?.startRenderingFromBottom === true &&
      !stickInitiallyEngagedRef.current
    ) {
      stickToBottomRef.current = true;
      stickInitiallyEngagedRef.current = true;
    }

    if (!stickToBottomRef.current) return;

    const contentDims = manager.layoutManager.getContentSize();
    const totalContent =
      firstItemOffsetRef.current +
      (horizontal ? contentDims.width : contentDims.height);
    const viewportSize = horizontal
      ? scroller.clientWidth
      : scroller.clientHeight;
    if (viewportSize === 0) return;
    if (totalContent <= viewportSize) return; // marginTop pin handles this case

    const target = totalContent - viewportSize;
    const current = horizontal ? scroller.scrollLeft : scroller.scrollTop;
    if (Math.abs(target - current) <= 0.5) return;

    lastProgrammaticScrollOffsetRef.current = target;
    if (horizontal) scroller.scrollLeft = target;
    else scroller.scrollTop = target;
  });

  // Once layout has settled (or data has changed), run a viewability pass so
  // the manager's last-context is populated. Without this, a consumer that
  // calls `recordInteraction()` before any scroll has occurred would see no
  // callback (the pipeline needs a scroll-offset + size snapshot to filter
  // against). Cheap — short-circuits when no `onViewableItemsChanged` is set.
  useEffect(() => {
    if (!mounted) return;
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const offset = horizontal ? scroller.scrollLeft : scroller.scrollTop;
    const viewport = horizontal ? scroller.clientWidth : scroller.clientHeight;
    manager.computeItemViewability(offset, viewport);
  }, [data, mounted, manager, horizontal]);

  // ResizeObserver bridges the gap when the scroller resizes without an
  // independent re-render (e.g. window resize, container collapses). It
  // just bumps a render; the per-render useLayoutEffect above picks up the
  // new dimensions.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const ObserverCtor = (
      globalThis as { ResizeObserver?: typeof ResizeObserver }
    ).ResizeObserver;
    if (!ObserverCtor) return;
    const observer = new ObserverCtor(() => bumpRender());
    observer.observe(scroller);
    return () => observer.disconnect();
  }, []);

  useImperativeHandle(ref, () => {
    const scrollAxisTo = (position: number, animated?: boolean) => {
      const scroller = scrollerRef.current;
      if (!scroller) return;
      const options: ScrollToOptions = {
        behavior: animated ? 'smooth' : 'auto',
      };
      if (horizontal) options.left = position;
      else options.top = position;
      scroller.scrollTo(options);
    };

    const absoluteIndexOffset = (
      index: number,
      viewPosition: number,
      viewOffset: number,
    ) => {
      const layout = manager.layoutManager.getLayout(index);
      const scroller = scrollerRef.current;
      const itemStart = horizontal
        ? layout.x
        : firstItemOffsetRef.current + layout.y;
      const itemSize = horizontal ? layout.width : layout.height;
      const viewportSize = horizontal
        ? (scroller?.clientWidth ?? 0)
        : (scroller?.clientHeight ?? 0);
      const positioned = itemStart - (viewportSize - itemSize) * viewPosition;
      return positioned - viewOffset;
    };

    const handle: FlashListRef<T> = {
      scrollToOffset({ offset, animated }: ScrollToOffsetOptions) {
        scrollAxisTo(offset, animated);
      },
      scrollToEnd(options?: ScrollToEndOptions) {
        const scroller = scrollerRef.current;
        if (!scroller) return;
        const maxScroll = horizontal
          ? scroller.scrollWidth - scroller.clientWidth
          : scroller.scrollHeight - scroller.clientHeight;
        scrollAxisTo(maxScroll, options?.animated);
      },
      scrollToIndex({
        index,
        animated,
        viewPosition = 0,
        viewOffset = 0,
      }: ScrollToIndexOptions) {
        if (index < 0 || index >= data.length) return;
        scrollAxisTo(
          absoluteIndexOffset(index, viewPosition, viewOffset),
          animated,
        );
      },
      scrollToItem({
        item,
        animated,
        viewPosition = 0,
        viewOffset = 0,
      }: ScrollToItemOptions<T>) {
        const index = manager.indexOfItem(item);
        if (index < 0) return;
        scrollAxisTo(
          absoluteIndexOffset(index, viewPosition, viewOffset),
          animated,
        );
      },
      getScrollResponder() {
        return scrollerRef.current;
      },
      recordInteraction() {
        captureViewabilityContext();
        manager.viewability.recordInteraction();
      },
      recomputeViewableItems() {
        captureViewabilityContext();
        manager.viewability.recomputeViewableItems();
      },
    };
    return handle;
  });

  // Reads the live scroll position + viewport from the DOM and feeds it into
  // the viewability pipeline. Used by `recordInteraction` /
  // `recomputeViewableItems` to make sure they have current context even
  // when no scroll has fired yet (e.g. consumer calls the ref method right
  // after mount). Also pushes the latest dimensions into the layout manager
  // so the helper sees a non-zero viewport — without this, a pre-mount
  // recordInteraction would read windowSize={0,0} and report nothing
  // viewable.
  const captureViewabilityContext = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const offset = horizontal ? scroller.scrollLeft : scroller.scrollTop;
    const width = scroller.clientWidth;
    const height = scroller.clientHeight;
    const viewport = horizontal ? width : height;
    if (viewport === 0) return;
    manager.setWindowSize({ width, height });
    manager.computeItemViewability(offset, viewport);
  };

  const handleScroll = () => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const offset = horizontal ? scroller.scrollLeft : scroller.scrollTop;
    const viewport = horizontal ? scroller.clientWidth : scroller.clientHeight;
    const contentSize = horizontal
      ? scroller.scrollWidth
      : scroller.scrollHeight;

    // Release the stick-to-bottom pin the first time the user genuinely
    // scrolls. We compare the observed offset against the last value we
    // set ourselves: a mismatch means the change came from the user.
    //
    // We deliberately do NOT clear `stickyAnchorRef` here — macOS
    // rubber-band fires extra scroll events whose offsets don't match our
    // programmatic target, and clearing on those would defeat anchor
    // preservation during the bounce. The sticky anchor is time-bounded
    // (see STICKY_ANCHOR_DURATION_MS) and clears itself once measurements
    // and bounce have settled.
    const expected = lastProgrammaticScrollOffsetRef.current;
    const matchesProgrammatic =
      Number.isFinite(expected) && Math.abs(expected - offset) <= 1;
    if (!matchesProgrammatic && stickToBottomRef.current) {
      stickToBottomRef.current = false;
    }

    // Keep windowSize current so dimension changes during scroll feed through
    // to the layout manager. Dimension capture itself runs in the
    // per-render useLayoutEffect below — matches upstream's onLayout-driven
    // re-measure pattern.
    manager.setWindowSize({
      width: scroller.clientWidth,
      height: scroller.clientHeight,
    });

    if (onScroll) {
      const event: ScrollEvent = { offset, viewport, contentSize };
      onScroll(event);
    }

    // Feed the tracker so its velocity history weights the next engaged
    // window's overscan toward the scroll direction. Order matters — record
    // before compute so this scroll's velocity is reflected.
    manager.tracker.recordScroll(offset);
    const newRange = manager.tracker.computeEngagedRange(offset, viewport);
    if (!sameRange(lastRenderedRangeRef.current, newRange)) {
      bumpRender();
    }

    // Treat each user scroll as an interaction (idempotent), then run
    // viewability. Matches upstream FlashList's behavior — the consumer can
    // still opt into a gated mode via `viewabilityConfig.waitForInteraction`
    // by also calling `recordInteraction` themselves at a later point.
    manager.viewability.recordInteraction();
    manager.computeItemViewability(offset, viewport);

    if (onEndReached) {
      const threshold = onEndReachedThreshold ?? DEFAULT_END_REACHED_THRESHOLD;
      const triggerDistance = viewport * threshold;
      const distanceFromEnd = contentSize - viewport - offset;
      if (distanceFromEnd <= triggerDistance) {
        if (!endReachedFiredRef.current) {
          endReachedFiredRef.current = true;
          onEndReached();
        }
      } else {
        endReachedFiredRef.current = false;
      }
    }

    if (onStartReached) {
      const threshold =
        onStartReachedThreshold ?? DEFAULT_START_REACHED_THRESHOLD;
      const triggerDistance = viewport * threshold;
      if (offset > triggerDistance) {
        startSeenAwayFromTopRef.current = true;
        startReachedFiredRef.current = false;
      } else if (
        startSeenAwayFromTopRef.current &&
        !startReachedFiredRef.current
      ) {
        startReachedFiredRef.current = true;
        onStartReached();
      }
    }
  };

  // Sync the render stack on every render. Idempotent and cheap (O(engaged
  // + active)). Pre-mount viewport=0 falls back to "render all" so SSR
  // emits the same DOM the client hydrates against.
  //
  // When maintain-position has queued a scroll adjustment, the live
  // scrollTop hasn't been updated yet but `useLayoutEffect` will set it
  // before paint. Factor that pending delta into the scrollOffset so the
  // engaged range matches the *next* viewport, not the stale one. Without
  // this, items mount at the old position and we paint the wrong cells for
  // a frame before the post-scroll re-render catches up.
  const scroller = scrollerRef.current;
  const rawScrollOffset = scroller
    ? horizontal
      ? scroller.scrollLeft
      : scroller.scrollTop
    : 0;
  const scrollOffset = rawScrollOffset + pendingScrollAdjustmentRef.current;
  const viewport = mounted
    ? ((horizontal ? scroller?.clientWidth : scroller?.clientHeight) ?? 0)
    : 0;
  const { stack, range } = manager.computeRenderStack(scrollOffset, viewport);
  lastRenderedRangeRef.current = range;

  const getLayout = (index: number) =>
    mounted ? manager.layoutManager.getLayout(index) : null;
  const renderItemForIndex = (index: number) =>
    renderItem({ item: data[index], index, target: 'Cell' });
  const isLastIndex = (index: number) => index === data.length - 1;

  const contentSize = mounted ? manager.layoutManager.getContentSize() : null;
  const showEmpty = data.length === 0;

  // Items-container: fix the scroll-axis dimension so the browser knows the
  // scrollable extent. The perpendicular dimension fills the scroller.
  // `flexShrink: 0` matters in horizontal mode — the content container is
  // a row-flex parent, and flex children default to `flex-shrink: 1` which
  // would compress the explicit `width` and break virtualization.
  // startRenderingFromBottom pads the items container so a short list
  // (content smaller than viewport) pins to the bottom of the viewport
  // instead of the top — a common chat-list polish for fewer-than-screenful
  // conversations. Computed off the layout manager's content size + the
  // scroller's viewport (read from the layout manager's window size since
  // that's already kept in sync by the scroll handler).
  const startRenderingFromBottom =
    maintainPositionEnabled &&
    maintainVisibleContentPosition?.startRenderingFromBottom === true;
  // The short-content pin can be opted out (`pinShortContentToBottom: false`)
  // to keep a fewer-than-screenful list TOP-aligned while still opening a
  // taller-than-viewport list at the bottom (the scroll re-snap above already
  // skips short content, so disabling only the pin yields "top until it scrolls").
  const pinShortContentToBottom =
    maintainVisibleContentPosition?.pinShortContentToBottom !== false;
  let renderFromBottomMargin = 0;
  if (
    startRenderingFromBottom &&
    pinShortContentToBottom &&
    mounted &&
    contentSize
  ) {
    const windowDims = manager.layoutManager.getWindowSize();
    const viewportLength = horizontal ? windowDims.width : windowDims.height;
    const contentLength = horizontal ? contentSize.width : contentSize.height;
    if (viewportLength > contentLength) {
      renderFromBottomMargin = viewportLength - contentLength;
    }
  }

  const itemsContainerStyle: Record<string, unknown> | undefined = mounted
    ? horizontal
      ? {
          position: 'relative' as const,
          width: contentSize?.width,
          height: '100%',
          flexShrink: 0,
          ...(renderFromBottomMargin > 0
            ? { marginLeft: renderFromBottomMargin }
            : {}),
        }
      : {
          position: 'relative' as const,
          height: contentSize?.height,
          width: '100%',
          ...(renderFromBottomMargin > 0
            ? { marginTop: renderFromBottomMargin }
            : {}),
        }
    : undefined;

  const hasStickyIndices =
    stickyHeaderIndices != null && stickyHeaderIndices.length > 0;
  const stickyHorizontalWarnedRef = useRef(false);
  if (hasStickyIndices && horizontal && !stickyHorizontalWarnedRef.current) {
    stickyHorizontalWarnedRef.current = true;
    console.warn(
      'FlashList: stickyHeaderIndices is not supported with horizontal lists; ignoring.',
    );
  }
  const stickyEnabled = hasStickyIndices && !horizontal;

  // Combined style for header/footer/empty wrappers. In horizontal mode they
  // sit alongside the items-container as flex children — make sure they
  // don't shrink. Inverted mode adds a per-wrapper un-flip.
  const accessoryWrapperStyle: Record<string, unknown> | undefined =
    horizontal || inverted
      ? {
          ...(inverted ? { transform: INVERTED_TRANSFORM } : {}),
          ...(horizontal ? { flexShrink: 0 } : {}),
        }
      : undefined;
  // pagingEnabled → CSS scroll-snap on the scroller. Combined with
  // scrollSnapAlign on each cell (set via the snapAlignment prop passed
  // down to ViewHolder). "mandatory" matches RN's pagingEnabled semantics:
  // the scroller always snaps to a cell, never resting between two.
  const resolvedSnapAlignment: 'start' | 'center' | 'end' | undefined =
    pagingEnabled ? (snapToAlignment ?? 'start') : undefined;
  const snapAxis = horizontal ? 'x' : 'y';
  const scrollerStyle: Record<string, unknown> | undefined =
    inverted || pagingEnabled
      ? {
          ...(inverted ? { transform: INVERTED_TRANSFORM } : {}),
          ...(pagingEnabled ? { scrollSnapType: `${snapAxis} mandatory` } : {}),
        }
      : undefined;

  // RN ScrollView API parity — accept these props but warn on web (no
  // direct CSS equivalent without injecting synthetic snap markers).
  const snapToIntervalWarnedRef = useRef(false);
  if (snapToInterval != null && !snapToIntervalWarnedRef.current) {
    snapToIntervalWarnedRef.current = true;
    console.warn(
      'FlashList: snapToInterval is not supported on web. Use pagingEnabled with cell sizes matching the desired stride instead.',
    );
  }
  const snapToOffsetsWarnedRef = useRef(false);
  if (snapToOffsets != null && !snapToOffsetsWarnedRef.current) {
    snapToOffsetsWarnedRef.current = true;
    console.warn(
      'FlashList: snapToOffsets is not supported on web. Use pagingEnabled with cells positioned at the desired offsets instead.',
    );
  }

  return (
    <div
      ref={scrollerRef}
      data-testid={SCROLLER_TEST_ID}
      className={cn(
        // A vertical list scrolls only vertically — pin the cross axis to
        // `hidden` so a slightly-too-wide row can't trigger a stray horizontal
        // scrollbar (CSS computes an unset cross axis to `auto` when the main
        // axis is `auto`/`scroll`). Consumers can still override via className.
        horizontal ? 'overflow-x-auto' : 'overflow-y-auto overflow-x-hidden',
        className,
      )}
      style={scrollerStyle}
      onScroll={handleScroll}
    >
      <div
        data-testid={CONTENT_TEST_ID}
        className={contentContainerClassName}
        style={
          horizontal
            ? {
                display: 'flex',
                flexDirection: 'row',
                height: '100%',
                // `max-content` lets the row's natural width (header +
                // items-container + footer) exceed the scroller; combined
                // with `overflow-x-auto` on the scroller that gives us the
                // horizontal scroll. Without it, the flex parent would
                // collapse to the scroller's width and compress children.
                width: 'max-content',
              }
            : undefined
        }
      >
        {stickyEnabled ? (
          <StickyHeaders
            scrollerRef={scrollerRef}
            stickyHeaderIndices={stickyHeaderIndices!}
            data={data}
            layoutManager={manager.layoutManager}
            renderItem={renderItem}
          />
        ) : null}
        {ListHeaderComponent ? (
          <div
            ref={headerRef}
            data-testid={HEADER_TEST_ID}
            style={accessoryWrapperStyle}
          >
            {resolveListChild(ListHeaderComponent)}
          </div>
        ) : null}
        {showEmpty ? (
          <div data-testid={EMPTY_TEST_ID} style={accessoryWrapperStyle}>
            {resolveListChild(ListEmptyComponent)}
          </div>
        ) : (
          <div data-testid={ITEMS_TEST_ID} style={itemsContainerStyle}>
            <ViewHolderCollection
              stack={stack}
              getLayout={getLayout}
              renderItemForIndex={renderItemForIndex}
              onMeasure={onMeasure}
              ItemSeparatorComponent={ItemSeparatorComponent}
              isLastIndex={isLastIndex}
              inverted={inverted}
              snapAlignment={resolvedSnapAlignment}
            />
          </div>
        )}
        {ListFooterComponent ? (
          <div data-testid={FOOTER_TEST_ID} style={accessoryWrapperStyle}>
            {resolveListChild(ListFooterComponent)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
